import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { C, t } from '../../Talent/Dashboard/components/dashboardConstants';
import { TALENT_PLACEHOLDER_SQUARE } from '../../../shared/utils/profilePlaceholders';
import ImageLightbox from '../../Talent/Profile/components/ImageLightbox';
import { saveApplicationRating, getApplicationDetail } from '../../../api/applicationApi';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import { useSavedItems } from '../../../contexts/SavedItemsContext';
import BoardPickerModal, { SavedConfirmModal } from '../../../shared/components/BoardPickerModal';

const TALENT_PLACEHOLDER = TALENT_PLACEHOLDER_SQUARE;

const BASE_TABS = ['소개', '필모그래피'];

function formatPhone(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return value;
}

function toInstagramUrl(value) {
  if (!value) return '';
  if (value.startsWith('http')) return value;
  return `https://www.instagram.com/${value.replace(/^@/, '')}/`;
}

function toYoutubeUrl(value) {
  if (!value) return '';
  if (value.startsWith('http')) return value;
  return `https://www.youtube.com/@${value.replace(/^@/, '')}`;
}

// ── 첨부자료 탭 UI ──────────────────────────────────────────────────────────

function ApplicationInfoTab({ applicationId }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    getApplicationDetail(applicationId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setIsLoading(false));
  }, [applicationId]);

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}><LoadingSpinner size="sm" /></div>;
  }
  if (!data) {
    return <div style={t('14px', 400, C.sub)}>첨부자료를 불러오지 못했습니다.</div>;
  }

  // 첨부파일 정규화
  let files = [];
  if (Array.isArray(data.attachments) && data.attachments.length > 0) {
    files = data.attachments.map(f => ({
      id: f.id ?? f.fileEntityId ?? null,
      name: f.originalFileName || f.fileName || f.name || '첨부파일',
      url: f.url || f.fileUrl || null,
      type: f.fileType || f.type || '',
    }));
  } else if (Array.isArray(data.attachmentUrls) && data.attachmentUrls.length > 0) {
    files = data.attachmentUrls.map((url, i) => ({
      id: i, name: url.split('/').pop() || `첨부파일 ${i + 1}`, url, type: '',
    }));
  }

  // 영상 링크 정규화
  const rawLinks = data.videoLinks || (data.videoLink ? [data.videoLink] : []);

  const isImg = (url, type) => {
    if (type && type.startsWith('IMAGE')) return true;
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(url || '');
  };

  const submittedAt = data.submittedAt ? data.submittedAt.slice(0, 10).replace(/-/g, '. ') : null;

  return (
    <>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* 지원 역할 + 제출일 */}
      {(data.roleName || submittedAt) && (
        <div style={{ display: 'flex', gap: '24px' }}>
          {data.roleName && (
            <div>
              <div style={{ ...t('12px', 400, C.sub), marginBottom: '4px' }}>지원 역할</div>
              <div style={t('14px', 600, C.bk)}>{data.roleName}</div>
            </div>
          )}
          {submittedAt && (
            <div>
              <div style={{ ...t('12px', 400, C.sub), marginBottom: '4px' }}>제출일</div>
              <div style={t('14px', 600, C.bk)}>{submittedAt}</div>
            </div>
          )}
        </div>
      )}

      {/* 자기소개 / 지원 메시지 */}
      <div>
        <div style={{ ...t('13px', 600, C.sub), marginBottom: '8px' }}>자기소개 / 지원 메시지</div>
        <div style={{ padding: '16px', background: C.white, border: `1px solid ${C.g200}`, borderRadius: '12px', ...t('14px', 400, C.bk), lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
          {data.message || '작성된 내용이 없습니다.'}
        </div>
      </div>

      {/* 영상 링크 */}
      {rawLinks.length > 0 && (
        <div>
          <div style={{ ...t('13px', 600, C.sub), marginBottom: '8px' }}>영상 링크</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {rawLinks.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', border: `1px solid ${C.g200}`, borderRadius: '10px', textDecoration: 'none', background: C.white }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <polygon points="5,3 19,12 5,21" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span style={{ ...t('13px', 400, C.bk), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{url}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* 첨부 파일 */}
      {files.length > 0 && (
        <div>
          <div style={{ ...t('13px', 600, C.sub), marginBottom: '8px' }}>첨부 파일</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {files.map((f, i) => {
              const img = isImg(f.url, f.type);
              return (
                <div key={f.id ?? i}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', border: `1px solid ${C.g200}`, borderRadius: '10px', background: C.white }}>
                  {/* 썸네일 */}
                  <div
                    onClick={img && f.url ? () => setLightboxUrl(f.url) : undefined}
                    style={{ width: '44px', height: '44px', borderRadius: '8px', border: `1px solid ${C.g200}`, background: C.g200, flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: img && f.url ? 'pointer' : 'default' }}>
                    {img && f.url
                      ? <img src={f.url} alt={f.name} loading="lazy" decoding="async" width={44} height={44} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                      : <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke={C.g400} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    }
                  </div>
                  {/* 파일명 */}
                  {img && f.url
                    ? <span onClick={() => setLightboxUrl(f.url)} style={{ ...t('13px', 500, C.bk), flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>{f.name}</span>
                    : f.url
                      ? <a href={f.url} target="_blank" rel="noreferrer" style={{ ...t('13px', 500, C.bk), flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none' }}>{f.name}</a>
                      : <span style={{ ...t('13px', 500, C.bk), flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  }
                </div>
              );
            })}
          </div>
        </div>
      )}

      {rawLinks.length === 0 && files.length === 0 && !data.message && (
        <div style={t('14px', 400, C.sub)}>제출된 추가 정보가 없습니다.</div>
      )}
    </div>

    <ImageLightbox url={lightboxUrl} alt="첨부 이미지" onClose={() => setLightboxUrl(null)} zIndex={100000} />
    </>
  );
}

function ContactTooltip({ label, anchorRect }) {
  if (!anchorRect) return null;
  return createPortal(
    <div style={{
      position: 'fixed',
      top: anchorRect.top - 8,
      left: anchorRect.left + anchorRect.width / 2,
      transform: 'translate(-50%, -100%)',
      padding: '5px 10px', background: '#090909', color: '#fff',
      fontSize: '11px', fontWeight: 500, borderRadius: '6px',
      whiteSpace: 'nowrap', zIndex: 200000, pointerEvents: 'none',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      fontFamily: 'Pretendard, sans-serif',
    }}>
      {label}
      <div style={{
        position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
        borderWidth: '4px', borderStyle: 'solid',
        borderColor: '#090909 transparent transparent transparent',
      }} />
    </div>,
    document.body
  );
}

function ContactSection({ actor }) {
  const [hoveredContact, setHoveredContact] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const [copiedType, setCopiedType] = useState(null);

  const contacts = [
    actor.contactEmail && { type: 'email', href: `mailto:${actor.contactEmail}`, label: actor.contactEmail },
    actor.phone && { type: 'phone', href: null, label: formatPhone(actor.phone) },
    actor.instagramUrl && { type: 'instagram', href: toInstagramUrl(actor.instagramUrl), label: actor.instagramUrl.startsWith('http') ? actor.instagramUrl : `@${actor.instagramUrl.replace(/^@+/, '')}` },
    actor.youtubeUrl && { type: 'youtube', href: toYoutubeUrl(actor.youtubeUrl), label: actor.youtubeUrl },
  ].filter(Boolean);

  if (contacts.length === 0) return null;

  const handleCopy = (text, type) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopiedType(type);
      setTimeout(() => setCopiedType(null), 1500);
    });
  };

  const handleMouseEnter = (e, type) => {
    setHoveredContact(type);
    setAnchorRect(e.currentTarget.getBoundingClientRect());
  };
  const handleMouseLeave = () => {
    setHoveredContact(null);
    setAnchorRect(null);
  };

  const boxStyle = {
    width: 36, height: 36, borderRadius: '8px',
    border: `1px solid ${C.g200}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    textDecoration: 'none', cursor: 'pointer', background: 'none',
    flexShrink: 0,
  };

  const hoveredItem = contacts.find(c => c.type === hoveredContact);
  const tooltipLabel = hoveredItem
    ? (copiedType === hoveredContact ? '복사됨!' : hoveredItem.label)
    : null;

  return (
    <div>
      <div style={{ ...t('13px', 600, C.sub), marginBottom: '8px' }}>Contact</div>
      <div style={{ display: 'flex', gap: '8px' }}>
        {contacts.map(({ type, href, label }) => {
          const iconEl = (
            <>
              {type === 'email' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="2" y="4" width="20" height="16" rx="2" stroke={C.sub} strokeWidth="1.5"/><path d="M2 8l10 7 10-7" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round"/></svg>}
              {type === 'phone' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round"/></svg>}
              {type === 'instagram' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="5" stroke={C.sub} strokeWidth="1.5"/><circle cx="12" cy="12" r="4" stroke={C.sub} strokeWidth="1.5"/><circle cx="17.5" cy="6.5" r="1" fill={C.sub}/></svg>}
              {type === 'youtube' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z" stroke={C.sub} strokeWidth="1.5"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" stroke={C.sub} strokeWidth="1.5" strokeLinejoin="round"/></svg>}
            </>
          );

          if (type === 'phone' || type === 'email') {
            return (
              <button key={type}
                onMouseEnter={(e) => handleMouseEnter(e, type)}
                onMouseLeave={handleMouseLeave}
                onClick={() => handleCopy(label, type)}
                style={boxStyle}
              >
                {iconEl}
              </button>
            );
          }
          return (
            <a key={type} href={href} target="_blank" rel="noreferrer"
              onMouseEnter={(e) => handleMouseEnter(e, type)}
              onMouseLeave={handleMouseLeave}
              style={boxStyle}
            >
              {iconEl}
            </a>
          );
        })}
      </div>
      {hoveredContact && tooltipLabel && (
        <ContactTooltip label={tooltipLabel} anchorRect={anchorRect} />
      )}
    </div>
  );
}

function HeartIcon({ filled }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? '#E94934' : C.g200}>
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
    </svg>
  );
}

function BookmarkIcon({ filled }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? C.bk : 'none'}>
      <path d="M19 21l-7-4-7 4V5a2 2 0 012-2h10a2 2 0 012 2z" stroke={C.bk} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <circle cx="18" cy="5" r="3" stroke={C.bk} strokeWidth="1.5"/>
      <circle cx="6" cy="12" r="3" stroke={C.bk} strokeWidth="1.5"/>
      <circle cx="18" cy="19" r="3" stroke={C.bk} strokeWidth="1.5"/>
      <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" stroke={C.bk} strokeWidth="1.5"/>
    </svg>
  );
}

const MAX_GALLERY_PHOTOS = 15;

export default function ActorDetailModal({ actor = {}, applicationId, onClose, actorList = [], onNavigate, onRatingChange, onMoveToNext, nextStageName }) {
  const [tab, setTab] = useState('소개');
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [imageModalUrl, setImageModalUrl] = useState(null);
  const [hearts, setHearts] = useState(actor.initialHearts ?? 0);
  const [shareCopied, setShareCopied] = useState(false);
  // 토스트를 버튼 안에 두면 상위 컨테이너의 overflow: hidden에 잘린다.
  // ContactTooltip처럼 body로 포털해 띄우기 위해 버튼 위치를 잡아 둔다.
  const shareBtnRef = useRef(null);
  const [shareAnchor, setShareAnchor] = useState(null);
  const navigate = useNavigate();
  const { talentMap, toggleSave, boardPickerState, savedConfirmState, selectBoard, createAndSelectBoard, closeBoardPicker, closeSavedConfirm } = useSavedItems();

  const medias = actor.medias || [];
  const profileCloseUpUrl = medias.find((m) => m.photoType === 'PROFILE_CLOSE_UP' && m.url)?.url ?? null;
  // PROFILE_CLOSE_UP 미디어가 실제로 있을 때만 프로필 이미지 사용
  const mainPhotoUrl = profileCloseUpUrl
    ?? (medias.length === 0 ? (actor.profileImageUrl ?? null) : null);
  const additionalPhotos = medias
    .filter((m) => m.mediaType === 'IMAGE' && m.photoType === 'PORTFOLIO' && m.url)
    .map((m) => m.url);

  const tags = actor.tags || (actor.skills || []).map((s) => s.name || s.skillName || s).filter(Boolean);
  const filmography = actor.filmography || actor.works || [];
  const contactEmail = actor.contactEmail || actor.email || actor.user?.email || '';
  const phone = actor.phone || actor.contactPhone || actor.user?.phone || '';
  const instagramUrl = actor.instagramUrl || '';
  const youtubeUrl = actor.youtubeUrl || '';
  const actorStorageId = String(actor.talentProfileId ?? actor.id ?? actor.name ?? 'unknown');
  // 지원자 경로(applicationId 있음)에서 actor.id는 '지원서 ID'라 배우 ID로 쓰면 안 된다.
  // 북마크 목록에서 연 경우엔 applicationId가 없고 actor.id가 곧 배우 프로필 ID다.
  const talentId = actor.talentProfileId ?? (applicationId ? null : actor.id) ?? null;
  const isSaved = talentId != null && Boolean(talentMap[String(talentId)]);
  const handleToggleSave = () => {
    if (talentId == null) return;
    toggleSave(talentId, 'TALENT_PROFILE');
  };
  const handleShare = async () => {
    const idPart = actor.slug || talentId;
    if (!idPart) { alert('이 지원자는 배우 프로필이 연결되어 있지 않아 공유할 수 없습니다.'); return; }
    const shareUrl = `${window.location.origin}/talents/${idPart}`;

    // navigator.clipboard는 보안 컨텍스트(HTTPS·localhost)에서만 있고, 권한·포커스 문제로
    // reject되기도 한다. 이전에는 catch가 없어 실패해도 아무 반응이 없었다.
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        throw new Error('clipboard API unavailable');
      }
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try { ok = document.execCommand('copy'); } catch { ok = false; }
      document.body.removeChild(ta);
      if (!ok) { window.prompt('아래 주소를 복사하세요', shareUrl); return; }
    }
    setShareAnchor(shareBtnRef.current?.getBoundingClientRect() ?? null);
    setShareCopied(true);
    setTimeout(() => { setShareCopied(false); setShareAnchor(null); }, 2000);
  };
  const modalRoot = typeof document !== 'undefined' ? document.body : null;
  const TABS = applicationId ? [...BASE_TABS, '첨부자료'] : BASE_TABS;
  const contacts = [
    contactEmail && { type: 'email', href: `mailto:${contactEmail}`, label: contactEmail },
    phone && { type: 'phone', href: `tel:${String(phone).replace(/\D/g, '')}`, label: formatPhone(phone) },
    instagramUrl && {
      type: 'instagram',
      href: toInstagramUrl(instagramUrl),
      label: instagramUrl.startsWith('http') ? instagramUrl : `@${instagramUrl.replace(/^@+/, '')}`,
    },
    youtubeUrl && { type: 'youtube', href: toYoutubeUrl(youtubeUrl), label: youtubeUrl },
  ].filter(Boolean);

  useEffect(() => {
    setHearts(actor.initialHearts ?? 0);
    const photos = (Array.isArray(actor.photos) && actor.photos.length > 0
      ? actor.photos
      : [mainPhotoUrl, ...additionalPhotos]
    ).filter(Boolean).slice(0, MAX_GALLERY_PHOTOS);
    setGalleryPhotos(photos);
    setActiveIndex(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actorStorageId]);

  const handleHeartChange = useCallback((value) => {
    setHearts(value);
    if (applicationId) {
      saveApplicationRating(applicationId, value).catch(() => {});
      if (onRatingChange) onRatingChange(applicationId, value);
    }
  }, [applicationId, onRatingChange]);

  // 키보드: 1~5 → 하트, ArrowLeft/Right → 이전/다음 지원자
  useEffect(() => {
    const handleKeyDown = (e) => {
      // input/textarea 포커스 중이면 무시
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key >= '1' && e.key <= '5') {
        handleHeartChange(Number(e.key));
      } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && onNavigate && actorList.length > 0) {
        const currentIdx = actorList.findIndex(a => {
          const sid = String(a.talentProfileId ?? a.id ?? a.name ?? 'unknown');
          return sid === actorStorageId;
        });
        if (currentIdx === -1) return;
        const nextIdx = e.key === 'ArrowLeft' ? currentIdx - 1 : currentIdx + 1;
        if (nextIdx >= 0 && nextIdx < actorList.length) {
          onNavigate(actorList[nextIdx]);
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => {
          if (galleryPhotos.length < 2) return prev;
          const dir = e.key === 'ArrowUp' ? -1 : 1;
          return (prev + dir + galleryPhotos.length) % galleryPhotos.length;
        });
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [applicationId, actorStorageId, actorList, galleryPhotos.length, handleHeartChange, onClose, onNavigate]);

  const renderedContent = (
    <>
    {shareCopied && <ContactTooltip label="링크 복사됨" anchorRect={shareAnchor} />}
    {boardPickerState && (
      <BoardPickerModal
        boards={boardPickerState.options}
        targetType={boardPickerState.targetType}
        onClose={closeBoardPicker}
        onSelect={selectBoard}
        onCreateAndSelect={createAndSelectBoard}
        /* 배우 상세 모달(99999)보다 위여야 한다 — 같은 값이면 뒤에 그려진 상세 모달에 가려진다 */
        zIndex={100010}
      />
    )}
    {savedConfirmState && (
      <SavedConfirmModal
        boardName={savedConfirmState.boardName}
        zIndex={100010}
        onClose={closeSavedConfirm}
        onNavigateToArchive={() => {
          try { localStorage.setItem('producer-bookmark-open-board', String(savedConfirmState.boardId)); } catch {}
          closeSavedConfirm();
          navigate('/producer/archive');
        }}
      />
    )}
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: C.bg, borderRadius: '21px', width: 'min(980px, calc(100vw - 48px))', height: 'calc(100vh - 48px)', maxHeight: '820px', display: 'flex', position: 'relative', padding: '48px', overflow: 'hidden' }}
      >
        <button onClick={onClose} style={{ position: 'absolute', top: 24, right: 24, background: 'none', border: 'none', cursor: 'pointer', zIndex: 1 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke={C.g400} strokeWidth="2" strokeLinecap="round"/></svg>
        </button>

        <div style={{ position: 'absolute', top: 24, right: 64, ...t('12px', 500, C.sub), textAlign: 'right', lineHeight: 1.5 }}>
          숫자키(1~5)로 지원자 분류 · ↑↓ 사진 전환
        </div>

        <div style={{ width: '320px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ width: '100%', aspectRatio: '3/4', background: C.g200, borderRadius: '20px', overflow: 'hidden' }}>
            <img
              src={galleryPhotos[activeIndex] || TALENT_PLACEHOLDER}
              alt={actor.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={e => { e.target.src = TALENT_PLACEHOLDER; e.target.style.objectFit = 'cover'; }}
            />
          </div>

          <div style={{ display: 'flex', gap: '6px', width: '100%', overflowX: 'auto', paddingBottom: '2px' }}>
            {galleryPhotos.map((url, i) => (
              <div
                key={url + i}
                onClick={() => setActiveIndex(i)}
                style={{
                  flexShrink: 0,
                  width: '52px',
                  aspectRatio: '1 / 1',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: `2px solid ${i === activeIndex ? C.bk : C.g200}`,
                  background: C.g200,
                  cursor: 'pointer',
                  opacity: i === activeIndex ? 1 : 0.7,
                }}
              >
                <img src={url} alt={`${actor.name} ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0, paddingLeft: '40px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              <h1 style={{ ...t('32px', 700, C.bk), margin: 0 }}>{actor.stageName || actor.name || '—'}</h1>
              {actor.stageName && actor.realName && (
                <span style={{ ...t('16px', 400, C.sub), whiteSpace: 'nowrap' }}>{actor.realName}</span>
              )}
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button
                onClick={handleToggleSave}
                disabled={talentId == null}
                title={talentId == null ? '배우 프로필이 연결되지 않아 저장할 수 없습니다' : undefined}
                style={{ width: 40, height: 40, borderRadius: '10px', border: `1px solid ${isSaved ? '#ffd384' : C.g200}`, background: isSaved ? '#fff7e8' : C.white, cursor: talentId == null ? 'not-allowed' : 'pointer', opacity: talentId == null ? 0.45 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                aria-label={isSaved ? '저장 해제' : '저장하기'}
              >
                <BookmarkIcon filled={isSaved} />
              </button>
              <button
                ref={shareBtnRef}
                onClick={handleShare}
                style={{ width: 40, height: 40, borderRadius: '10px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <ShareIcon />
              </button>
              {onMoveToNext && nextStageName && (
                <button
                  onClick={onMoveToNext}
                  style={{ height: 40, padding: '0 14px', borderRadius: '10px', border: 'none', background: '#FFD92B', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', ...t('14px', 700, C.bk), whiteSpace: 'nowrap', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)';
                    e.currentTarget.style.boxShadow = '0px 0px 40px rgba(255, 255, 255, 0.57)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#FFD92B';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {nextStageName}{/[ㄱ-ㅎㅏ-ㅣ가-힣]$/.test(nextStageName) && (nextStageName.charCodeAt(nextStageName.length - 1) - 0xAC00) % 28 === 0 ? '로' : '으로'} 통과
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke={C.bk} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              )}
            </div>
          </div>

          {applicationId && (
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={t('15px', 600, C.bk)}>평가 :</span>
              <div style={{ display: 'flex', gap: '2px' }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <span key={i} onClick={() => handleHeartChange(i)} style={{ cursor: 'pointer' }}>
                    <HeartIcon filled={i <= hearts} />
                  </span>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0', marginBottom: '14px', background: C.bg, borderRadius: '12px', padding: '12px 16px' }}>
            {[
              { label: '성별', value: actor.gender || '-' },
              { label: '나이', value: actor.age || '-' },
              { label: '키', value: actor.height || '-' },
              { label: '몸무게', value: actor.weight || '-' },
            ].map((item, i, arr) => (
              <div key={item.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px', borderRight: i < arr.length - 1 ? `1px solid ${C.g200}` : 'none', padding: '0 14px', paddingLeft: i === 0 ? 0 : '14px' }}>
                <span style={t('11px', 400, C.sub)}>{item.label}</span>
                <span style={t('14px', 600, C.bk)}>{item.value}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', borderBottom: `1px solid ${C.g200}`, marginBottom: '16px', flexShrink: 0 }}>
            {TABS.map((tb) => (
              <button key={tb} onClick={() => setTab(tb)} style={{ padding: '8px 16px', background: 'none', border: 'none', borderBottom: `2px solid ${tab === tb ? C.bk : 'transparent'}`, cursor: 'pointer', marginBottom: '-1px', ...t('14px', tab === tb ? 700 : 400, tab === tb ? C.bk : C.sub) }}>
                {tb}
              </button>
            ))}
          </div>

          {tab === '소개' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', flex: 1, overflowY: 'auto', paddingBottom: '4px' }}>
              {/* 자기소개 — 3줄 고정 */}
              <div>
                <div style={{ ...t('13px', 600, C.sub), marginBottom: '5px' }}>자기소개</div>
                <p style={{ ...t('14px', 400, C.bk), margin: 0, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {actor.bio || '소개가 없습니다.'}
                </p>
              </div>

              {/* 특기 */}
              <div>
                <div style={{ ...t('13px', 600, C.sub), marginBottom: '5px' }}>특기</div>
                {tags.length > 0 ? (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {tags.map((tag) => (
                      <span key={tag} style={{ padding: '4px 10px', borderRadius: '100px', border: `1px solid ${C.g200}`, ...t('12px', 500, C.bk) }}>{tag}</span>
                    ))}
                  </div>
                ) : (
                  <span style={t('14px', 400, C.bk)}>-</span>
                )}
              </div>

              {/* 소속 / 거주지 — 한 줄 */}
              <div style={{ display: 'flex', gap: '24px' }}>
                <div>
                  <div style={{ ...t('13px', 600, C.sub), marginBottom: '3px' }}>소속</div>
                  <span style={t('14px', 400, C.bk)}>{actor.agency || '-'}</span>
                </div>
                <div>
                  <div style={{ ...t('13px', 600, C.sub), marginBottom: '3px' }}>거주지</div>
                  <span style={t('14px', 400, C.bk)}>{actor.residence || actor.location || '-'}</span>
                </div>
              </div>

              {contacts.length > 0 && (
                <div>
                  <div style={{ ...t('15px', 600, C.sub), marginBottom: '8px' }}>연락처</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {contacts.map(({ type, href, label }) => {
                      const icon = (
                        <>
                          {type === 'email' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="2" y="4" width="20" height="16" rx="2" stroke={C.sub} strokeWidth="1.5"/><path d="M2 8l10 7 10-7" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round"/></svg>}
                          {type === 'phone' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round"/></svg>}
                          {type === 'instagram' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="5" stroke={C.sub} strokeWidth="1.5"/><circle cx="12" cy="12" r="4" stroke={C.sub} strokeWidth="1.5"/><circle cx="17.5" cy="6.5" r="1" fill={C.sub}/></svg>}
                          {type === 'youtube' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z" stroke={C.sub} strokeWidth="1.5"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" stroke={C.sub} strokeWidth="1.5" strokeLinejoin="round"/></svg>}
                        </>
                      );

                      return (
                        <a
                          key={`${type}-${label}`}
                          href={href}
                          target={type === 'email' || type === 'phone' ? '_self' : '_blank'}
                          rel="noreferrer"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            width: 'fit-content',
                            maxWidth: '100%',
                            padding: '10px 12px',
                            borderRadius: '10px',
                            border: `1px solid ${C.g200}`,
                            textDecoration: 'none',
                            background: C.white,
                          }}
                        >
                          <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
                          <span style={{ ...t('14px', 400, C.bk), wordBreak: 'break-all' }}>{label}</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === '첨부자료' && applicationId && (
            <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '4px' }}>
              <ApplicationInfoTab applicationId={applicationId} />
            </div>
          )}

          {tab === '필모그래피' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', flex: 1, paddingBottom: '4px' }}>
              {filmography.length === 0 ? (
                <div style={t('15px', 400, C.sub)}>등록된 필모그래피가 없습니다.</div>
              ) : (
                filmography.map((film, i) => {
                  const cardImageUrl = film.stillcutUrl || film.imageUrl || null;
                  const filmTitle = film.title || '';
                  const filmRole = film.role || film.roleName || '';
                  const filmYear = String(film.year || film.releaseYear || '');
                  const filmVideoUrl = film.videoUrl || film.youtubeUrl || null;

                  return (
                    <div key={i} style={{ padding: '16px 20px', border: `1px solid ${C.g200}`, borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
                        {cardImageUrl && (
                          <div
                            onClick={() => setImageModalUrl(cardImageUrl)}
                            style={{ width: '64px', height: '80px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, cursor: 'pointer', border: `1px solid ${C.g200}` }}
                          >
                            <img src={cardImageUrl} alt={filmTitle} loading="lazy" decoding="async" width={64} height={80} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={t('16px', 600, C.bk)}>{filmTitle}</div>
                          <div style={{ ...t('13px', 400, C.sub), marginTop: '4px' }}>{filmRole}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        {filmVideoUrl && (
                          <button
                            onClick={() => window.open(filmVideoUrl, '_blank')}
                            style={{ width: 32, height: 32, borderRadius: '8px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                            title="영상 보기"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.97C18.88 4 12 4 12 4s-6.88 0-8.59.45A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58 2.78 2.78 0 001.95 1.97C5.12 20 12 20 12 20s6.88 0 8.59-.45a2.78 2.78 0 001.95-1.97A29 29 0 0023 12a29 29 0 00-.46-5.58z" stroke={C.sub} strokeWidth="1.5"/>
                              <path d="M9.75 15.02l5.75-3.02-5.75-3.02v6.04z" fill={C.sub}/>
                            </svg>
                          </button>
                        )}
                        <span style={{ padding: '4px 10px', borderRadius: '8px', background: C.g200, ...t('13px', 500, C.bk) }}>{filmYear || '-'}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      <ImageLightbox
        url={imageModalUrl}
        alt="필모그래피 스틸컷"
        onClose={() => setImageModalUrl(null)}
        zIndex={100000}
      />
    </div>
    </>
  );

  if (!modalRoot) return null;
  return createPortal(renderedContent, modalRoot);
}
